import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from '../../toasts';
import ContractService from '../../services/contract.service';
import { getError, showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button } from '../../components/EntityList';

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AssetContracts({ assetId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const [deletingContract, setDeletingContract] = useState(null);

	const handleEditContract = (contract) => {
		navigate(`/AdminScreen/assets/asset/${assetId}/contracts/${contract._id}/edit`);
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((contract) => {
			const key = contract.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (contract.amount || 0));
		});
		return [
			{ label: t('entity.contract.plural'), value: rows.length },
			{
				label: t('field.amount'),
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	async function handleConfirmDelete() {
		try {
			await ContractService.delete(deletingContract._id);
			toast.success(t('msg.contractDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingContract(null);
		}
	}

	const columns = [
		{ key: 'id', header: 'ID', render: (contract) => <IdChip>{contract.customId}</IdChip> },
		{ key: 'currency', header: t('field.currency'), className: 'tw:font-mono', render: (contract) => contract.currency },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (contract) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(contract.currency, contract.amount)}</span>,
		},
		{ key: 'periodInit', header: t('field.periodInit'), render: (contract) => formatDate(contract.periodInit) },
		{ key: 'periodEnd', header: t('field.periodEnd'), render: (contract) => formatDate(contract.periodEnd) },
		{
			key: 'tenant',
			header: t('entity.tenant'),
			render: (contract) =>
				contract.owner ? (
					<Link to={`/AdminScreen/accounts/account/${contract.owner._id || contract.owner}`} onClick={(e) => e.stopPropagation()}>
						{contract.owner.name}
					</Link>
				) : null,
		},
		{ key: 'folder', header: t('field.folder'), render: (contract) => contract.folder },
	];

	const renderRowActions = (contract) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditContract(contract) },
				{ label: t('action.delete'), onClick: () => setDeletingContract(contract), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={() => navigate(`/AdminScreen/assets/asset/${assetId}/newContract`)}>
					{t('action.add')} {t('entity.contract')}
				</Button>
			</div>
			<Section title={t('entity.contract.plural')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(contract) => navigate(`/AdminScreen/contracts/contract/${contract._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noData')}
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingContract)}
				handleClose={() => setDeletingContract(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteContract')}
				description={t('msg.confirmDeleteContract')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AssetContracts;
